import { C_M, CODE_MESSAGE, IS_BROWSER } from '@/constants';
import axios, { type InternalAxiosRequestConfig, type AxiosRequestConfig, AxiosHeaders, type AxiosResponse } from 'axios';
import dayjs from 'dayjs';
import localforage from 'localforage';

/**
 * 配置request请求时的默认参数
 */
const requestConfig = {
  baseURL: 'api',
  timeout: 15 * 1000,
  contentType: 'json',
  timeoutErrorMessage: '请求超时',
  responseEncoding: 'UTF-8',
};

const CAN_SEND_METHOD = ['post', 'put', 'delete', 'POST', 'PUT', 'DELETE'];
// 请求拦截器
axios.interceptors.request.use(async (config: InternalAxiosRequestConfig & TOption) => {
  const _config = Object.assign({}, requestConfig, config);
  const method = _config.method as string;
  const canSend = CAN_SEND_METHOD.includes(method);
  if (!_config.headers) {
    _config.headers = new AxiosHeaders();
  }
  if (!!process.env.NEXT_PUBLIC_GRAY_TAG) {
    _config.headers.grayTag = process.env.NEXT_PUBLIC_GRAY_TAG;
  }
  if (!IS_BROWSER) {
    _config.headers['x-appid'] = process.env.NEXT_PUBLIC_FW_APPID;
    if (_config.baseURL == 'api') {
      _config.baseURL = process.env.NEXT_PUBLIC_API_HOST ?? requestConfig.baseURL;
    }
    if (_config.baseURL == 'api2') {
      _config.baseURL = process.env.NEXT_PUBLIC_FILE_HOST ?? requestConfig.baseURL;
    }
  }
  await prepareAuth(_config);
  if (canSend) {
    const _ct = C_M.get(_config.contentType);
    if (_ct) {
      _config.headers['Content-Type'] = _ct;
    }
    if (_config.contentType == 'json') {
      const _p = _config.params;
      _config.data = _p;
      _config.params = undefined;
    }
    if (_config.contentType == 'form-data') {
      _config.transformRequest = data => {
        if (data instanceof FormData) {
          return data;
        }
        const f = new FormData();
        Object.entries(data).forEach(([key, value]: any[]) => {
          f.append(key, value);
        });
        return f;
      };
    }
  }
  return _config;
});

async function prepareAuth(config: PostOption | TOption) {
  if (!config.headers) {
    config.headers = {};
  }
  const path = config.url ?? '';
  if (!path.startsWith('http')) {
    if (IS_BROWSER && !config?.noSign) {
      let _p = path.split('?')[0];
      if (_p.startsWith('/api/')) {
        _p = _p.slice(4);
      }
      const idf = (await localforage.getItem<number>(process.env.NEXT_PUBLIC_CALKEY ?? 'i_dif')) || 0;
      console.debug('idf: %d', idf);
      const { eligo } = await import('@feewee/helper-wasm');
      const _n = eligo(_p, `${Date.now() + idf}`);
      config.headers['x-sign'] = _n;
    }

    if (!IS_BROWSER && !config?.noSign) {
      if (process.env.NEXT_PUBLIC_SIGN) {
        config.headers['x-sign'] = process.env.NEXT_PUBLIC_SIGN;
      }
    }
  }
  const token = config.token;
  if (token) {
    config.headers.Authorization = token;
  }
  if (config.ignoreAuth) {
    config.headers.Authorization = undefined;
    config.headers['x-ignore-auth'] = true;
  }
}

// 响应拦截器
axios.interceptors.response.use(
  (response: AxiosResponse<TResponse, PostOption | TOption>) => {
    const { data } = response;
    if (response.status === 200) {
      if (!IS_BROWSER) {
        const api = response?.config?.url;
        const _token = response?.config?.headers?.Authorization;
        console.info('========================================request========================================');
        console.info(
          '[%s] API: %s \ntoken: %s \nresponse: %s',
          dayjs().format('YYYY-MM-DD HH:mm:ss.SSS'),
          api,
          _token,
          JSON.stringify(data).slice(0, 500) + (JSON.stringify(data).length > 500 ? '...' : ''),
        );
        console.info('\n');
      }
      if (typeof data?.success !== 'boolean') {
        return response;
      }
      if (data?.success) {
        return response;
      }
    }
    if (data) {
      const code = data?.code || response.status;
      const message = data?.result || CODE_MESSAGE[code] || response.statusText;
      const error = new Error(message);
      // @ts-expect-error 忽略这个问题
      error.code = code;
      return Promise.reject(error);
    }
    return response;
  },
  (e: any) => {
    const response: AxiosResponse<any> = e.response;
    const code = response?.status;
    let message = CODE_MESSAGE[code];
    if (code == 409) {
      message = response?.data?.result;
    }
    if (message) {
      e.message = message;
    }
    if (!IS_BROWSER) {
      const api = response?.config?.url;
      const _token = response?.config?.headers?.Authorization;
      console.info('========================================request========================================');
      console.info('[%s] API: %s \ntoken: %s \n%s %s', dayjs().format('YYYY-MM-DD HH:mm:ss.SSS'), api, _token, response.status, response.statusText);
      console.info('\n');
    }
    return Promise.reject(e);
  },
);

async function get<T, P = any>(api: string, params?: P, options?: TOption & AxiosRequestConfig) {
  //@ts-expect-error 兼容之前项目的写法
  const _p = params?.params ?? params;
  const _options = options || {};
  const res = await axios<TResponse<T>>(api, {
    method: 'GET',
    params: _p,
    ...requestConfig,
    ..._options,
  });
  const { data } = res;
  if (options?.intactResponse) {
    return (data ?? res) as T;
  }
  return data?.data as T;
}

async function post<T, P = any>(api: string, params?: P, options?: PostOption) {
  const _options = options || {};
  const res = await axios<TResponse<T>>(api, {
    method: 'POST',
    params,
    ...requestConfig,
    ..._options,
  });
  const { data } = res;
  if (options?.intactResponse) {
    return (data ?? res) as T;
  }
  return data?.data as T;
}

async function put<T, P = any>(api: string, params?: P, options?: PostOption) {
  const _options = options || {};
  const res = await axios<TResponse<T>>(api, {
    method: 'PUT',
    params,
    ...requestConfig,
    ..._options,
  });
  const { data } = res;
  if (options?.intactResponse) {
    return (data ?? res) as T;
  }
  return data?.data as T;
}

async function del<T, P = any>(api: string, params?: P, options?: PostOption) {
  const _options = options || {};
  const res = await axios<TResponse<T>>(api, {
    method: 'DELETE',
    params,
    ...requestConfig,
    ..._options,
  });
  const { data } = res;
  if (options?.intactResponse) {
    return (data ?? res) as T;
  }
  return data?.data as T;
}

async function upload<T, P = any>(api: string, params?: P, options?: PostOption) {
  const _options = { ...options, contentType: 'form-data' };
  const res = await axios<TResponse<T>>(api, {
    method: 'POST',
    params,
    ...requestConfig,
    baseURL: 'api2',
    ..._options,
    timeout: 60 * 60 * 1000,
  });
  const { data } = res;
  if (options?.intactResponse) {
    return (data ?? res) as T;
  }
  return data?.data as T;
}

const NextAxios = { get, post, put, del, upload };

export default NextAxios;
